import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoTechnicalVisitReports from "config/demo/technicalVisitReports.json";
import TechnicalVisitReportForm from "components/agro/technicalVisitReports/TechnicalVisitReportForm";

const TechnicalVisitReports = () => {
  const [reports] = useState(
    demoTechnicalVisitReports && demoTechnicalVisitReports.slice
      ? demoTechnicalVisitReports.slice(0, 10)
      : []
  );
  const [reportDetails, setReportDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      reports.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={
                reportDetails?.code === item.code
                  ? strings.reports.hide
                  : strings.reports.show
              }
              icon={
                reportDetails?.code === item.code
                  ? "VisibilityOff"
                  : "Visibility"
              }
              color="primary"
              onClick={() => {
                reportDetails?.code === item.code
                  ? setReportDetails(null)
                  : setReportDetails(item);
              }}
            />
            <ActionIcon
              name={strings.reports.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", item.code)}
            />
            <ActionIcon
              name={strings.reports.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: item.code,
                  itemName: item.description || item.code,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reports, reportDetails?.code]
  );

  const openModal = (action, code = null) =>
    setModalForm({ visible: true, action, itemId: code });

  const tableColumns = [
    {
      key: "code",
      label: strings.table.headers.code,
      _style: { width: "12%" },
    },
    {
      key: "date",
      label: strings.table.headers.date,
      _style: { width: "16%" },
    },
    {
      key: "performedById",
      label: strings.table.headers.performedBy,
      _style: { width: "22%" },
    },
    {
      key: "farmId",
      label: strings.table.headers.farm,
      _style: { width: "20%" },
    },
    {
      key: "status",
      label: strings.table.headers.status,
      _style: { width: "10%" },
    },
    { key: "actions", label: "", _style: { width: "12%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.reports.addReport}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
          />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!reportDetails?.code} style={styles.collapseCard}>
          <CCard key={reportDetails?.code}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "
                {reportDetails?.description || reportDetails?.code}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.code}:</b> {reportDetails?.code ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.reportNumber}:</b>{" "}
                {reportDetails?.reportNumber ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.date}:</b>{" "}
                {reportDetails?.date
                  ? moment(reportDetails.date).format("YYYY-MM-DD")
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.performedBy}:</b>{" "}
                {reportDetails?.performedByName ??
                  reportDetails?.performedById ??
                  "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.farm}:</b>{" "}
                {reportDetails?.farmName ?? reportDetails?.farmId ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.lot}:</b>{" "}
                {reportDetails?.lotName ?? reportDetails?.lotId ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.photoEvidence}:</b>{" "}
                {(Array.isArray(reportDetails?.photoEvidence)
                  ? reportDetails.photoEvidence.join(", ")
                  : reportDetails?.photoEvidence) ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.recommendations}:</b>{" "}
                {reportDetails?.recommendations ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {reportDetails?.description ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.status}:</b> {reportDetails?.status ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.observations}:</b>{" "}
                {reportDetails?.observations ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {reportDetails?.createdAt
                  ? moment(reportDetails.createdAt).format(
                      "YYYY-MM-DD HH:mm:ss"
                    )
                  : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.itemName,
        })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalForm.visible}
          onClose={() => setModalForm({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalForm.action === "create"
                ? strings.modalForm.addTitle
                : strings.modalForm.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <TechnicalVisitReportForm
                type={modalForm.action}
                initialValues={
                  modalForm.action === "update"
                    ? reports.find((r) => r.code === modalForm.itemId) || {}
                    : {}
                }
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Informes Técnicos de Visita",
    reports: {
      addReport: "Registrar Informe",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        code: "Código",
        date: "Fecha",
        performedBy: "Agrónomo",
        farm: "Finca",
        lot: "Lote",
        status: "Estado",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles Informe Técnico",
      code: "Código",
      reportNumber: "Número de Informe",
      date: "Fecha",
      performedBy: "Agrónomo",
      farm: "Finca",
      lot: "Lote",
      photoEvidence: "Evidencia fotográfica",
      recommendations: "Recomendaciones",
      description: "Descripción",
      status: "Estado",
      observations: "Observaciones",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el informe {name}?",
    },
    modalForm: {
      addTitle: "Registrar Informe Técnico",
      updateTitle: "Actualizar Informe Técnico",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default TechnicalVisitReports;
